import {
    Button,
    Center,
    Checkbox,
    Container,
    Divider,
    Group,
    Paper,
    PasswordInput,
    Stack,
    TextInput,
    Title,
} from '@mantine/core';
import { IconLogin2, IconPlayerRecord, IconVideoPlus } from '@tabler/icons-react';
import { type SubmitEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { encodePassphrase, randomString } from '@/shared/client-utils';
import { LanguageSwitcher } from '@/shared/language-switcher';
import { notifyError } from '@/shared/notify';
import { useSessionUser } from '@/shared/use-session-user';
import { UserMenu } from '@/shared/user-menu';

export function HomePage() {
    const { t } = useTranslation('home');
    const navigate = useNavigate();
    const [e2ee, setE2ee] = useState(false);
    const [sharedPassphrase, setSharedPassphrase] = useState(() => randomString(64));
    const [roomCode, setRoomCode] = useState('');
    const [starting, setStarting] = useState(false);
    const user = useSessionUser();
    const isGuest = user?.kind === 'guest';
    const trimmedRoomCode = roomCode.trim();

    const startMeeting = () => {
        setStarting(true);
        fetch('/api/meetings', { method: 'POST' })
            .then(async (response) => {
                if (!response.ok) throw new Error(await response.text());
                const { roomName } = (await response.json()) as { roomName: string };
                const roomPath = `/${encodeURIComponent(roomName)}`;
                await navigate(e2ee ? `${roomPath}#${encodePassphrase(sharedPassphrase)}` : roomPath);
            })
            .catch((error: unknown) => {
                setStarting(false);
                notifyError(t('startError'), error);
            });
    };

    const joinMeeting = (event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!trimmedRoomCode) return;
        void navigate(`/${encodeURIComponent(trimmedRoomCode)}`);
    };

    return (
        <Center mih="100dvh" p="md">
            <Container p={0} size="xs" w="100%">
                <Paper withBorder p="xl">
                    <Stack gap="lg">
                        <Group justify="space-between" wrap="nowrap">
                            <Title order={2}>{t('title')}</Title>
                            <Group gap="var(--spacing-2)" wrap="nowrap">
                                <LanguageSwitcher />
                                <UserMenu />
                            </Group>
                        </Group>
                        {!isGuest && (
                            <>
                                <Stack gap="md">
                                    <Button
                                        leftSection={<IconVideoPlus size={20} />}
                                        loading={starting}
                                        variant="filled"
                                        onClick={startMeeting}
                                    >
                                        {t('start')}
                                    </Button>
                                    <Checkbox
                                        checked={e2ee}
                                        label={t('e2ee')}
                                        onChange={(event) => {
                                            setE2ee(event.currentTarget.checked);
                                        }}
                                    />
                                    {e2ee && (
                                        <PasswordInput
                                            label={t('passphrase')}
                                            value={sharedPassphrase}
                                            onChange={(event) => {
                                                setSharedPassphrase(event.currentTarget.value);
                                            }}
                                        />
                                    )}
                                </Stack>
                                <Divider />
                            </>
                        )}
                        <form onSubmit={joinMeeting}>
                            <Group align="flex-end" gap="sm" wrap="nowrap">
                                <TextInput
                                    label={t('roomCode')}
                                    placeholder={t('roomCodePlaceholder')}
                                    style={{ flex: 1 }}
                                    value={roomCode}
                                    onChange={(event) => {
                                        setRoomCode(event.currentTarget.value);
                                    }}
                                />
                                <Button
                                    disabled={!trimmedRoomCode}
                                    leftSection={<IconLogin2 size={20} />}
                                    type="submit"
                                    variant="light"
                                >
                                    {t('join')}
                                </Button>
                            </Group>
                        </form>
                        {!isGuest && (
                            <>
                                <Divider />
                                <Button
                                    component={Link}
                                    leftSection={<IconPlayerRecord size={20} />}
                                    to="/recordings"
                                    variant="subtle"
                                >
                                    {t('recordings')}
                                </Button>
                            </>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Center>
    );
}
